import { Link } from '@tanstack/react-router';
import { ChevronRight, Loader2 } from 'lucide-react';

import { useGetFamilyHub } from '~entities/family';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

type StudentDeepLink = {
  label: string;
  pathSuffix: 'classes' | 'new-payments' | 'notes' | 'details';
};

const LINKS: Record<string, StudentDeepLink> = {
  classes: { label: 'Classes', pathSuffix: 'classes' },
  transactions: {
    label: 'Transactions and Billing',
    pathSuffix: 'new-payments',
  },
  notes: { label: 'Notes', pathSuffix: 'notes' },
};

const studentPathFor = (
  pathSuffix: StudentDeepLink['pathSuffix'],
  studentId: string,
) => {
  if (pathSuffix === 'classes') {
    return {
      to: '/students/$studentId/classes' as const,
      params: { studentId },
    };
  }
  if (pathSuffix === 'new-payments') {
    return {
      to: '/students/$studentId/new-payments' as const,
      params: { studentId },
    };
  }
  if (pathSuffix === 'notes') {
    return {
      to: '/students/$studentId/notes' as const,
      params: { studentId },
    };
  }
  return {
    to: '/students/$studentId/details' as const,
    params: { studentId },
  };
};

/**
 * V2 hub tabs that are still student-scoped deep-link into each student
 * until family-aggregated views exist. Rows are clickable (no Open button spam).
 */
export const FamilyHubStudentLinks = ({
  familyId,
  tab,
}: {
  familyId: string;
  tab: keyof typeof LINKS;
}) => {
  const { data, isLoading, isError } = useGetFamilyHub(familyId);
  const link = LINKS[tab];

  if (isLoading) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading…
      </div>
    );
  }

  if (isError || !data || !link) {
    return <p className='text-sm text-destructive'>Could not load students.</p>;
  }

  return (
    <Card className='max-w-2xl'>
      <CardHeader>
        <CardTitle className='text-lg'>{link.label}</CardTitle>
        <p className='text-sm text-muted-foreground'>
          Select a student to view {link.label.toLowerCase()}. Family-level
          roll-ups will land here later.
        </p>
      </CardHeader>
      <CardContent className='space-y-1'>
        {data.students.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No students on this family.
          </p>
        ) : (
          data.students.map((student) => {
            const path = studentPathFor(link.pathSuffix, student._id);
            return (
              <Link
                key={student._id}
                to={path.to}
                params={path.params}
                className='flex items-center justify-between gap-2 rounded-md border border-transparent px-3 py-3 text-left transition-colors hover:border-border hover:bg-muted/40'
              >
                <span className='font-medium'>
                  {student.name ?? 'Student'}
                </span>
                <ChevronRight
                  className='size-4 shrink-0 text-muted-foreground'
                  aria-hidden
                />
              </Link>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};
